import * as css from 'css/exports.module.scss';

import './TeamInvites.scss';

import { CheckCircleOutlined } from '@ant-design/icons';
import difference from 'lodash/difference';
import { UserInvitation } from '@sonar-software/sonar-security';
import { When } from 'react-if';

import { Button, Col, Form, FormInstance, Row, Space } from 'antd';
import { Email, FirstName, LastName } from '@ui/form-items';
import { NOTIFICATIONS_COPY, ONBOARDING_SYNC_COPY } from '@sonar-software/sonar-ts-constants';
import React, { useCallback, useEffect, useState } from 'react';
import { useInvite, usePrevious } from '@hooks';

interface InviteRecord {
	email?: string;
	firstName?: string;
	index: number;
	lastName?: string;
	processed?: boolean;
}

const defaultRecords: InviteRecord[] = [ { index: 0 }, { index: 1 } ];

function hasRequiredValues(invite: InviteRecord): boolean {
	return Boolean(invite.firstName) && Boolean(invite.lastName) && Boolean(invite.email);
}

function hasValidValues(invite: InviteRecord, form: FormInstance): boolean {
	const { index } = invite;
	const isFirstNameValid = form.getFieldError(`firstName-${index}`).length === 0;
	const isLastNameValid = form.getFieldError(`lastName-${index}`).length === 0;
	const isEmailValid = form.getFieldError(`email-${index}`).length === 0;

	return isFirstNameValid && isLastNameValid && isEmailValid;
}

function getInvitesToSend(invites: InviteRecord[], form: FormInstance): InviteRecord[] {
	return invites.filter((invite) => !invite.processed && hasRequiredValues(invite) && hasValidValues(invite, form));
}

function rebuildInvites(currentInvites: InviteRecord[], name: string, value: string): InviteRecord[] {
	const nameParts = name.split('-');
	const inputIndex = Number(nameParts[ 1 ]);

	const targetInvite = currentInvites[ inputIndex ];
	const newInvites = [
		...currentInvites.slice(0, inputIndex),
		{
			...targetInvite,
			[ nameParts[ 0 ] ]: value.trim()
		},
		...currentInvites.slice(inputIndex + 1)
	];

	if (inputIndex === currentInvites.length - 1) {
		newInvites.push({ index: currentInvites.length });
	}

	return newInvites;
}

function rebuildInvitesWithProcessed(currentInvites: InviteRecord[], validInvites: InviteRecord[]): InviteRecord[] {
	return currentInvites.map((invite) => {
		if (validInvites.some((validInvite) => validInvite.index === invite.index)) {
			return {
				...invite,
				processed: true
			};
		}

		return invite;
	});
}

function displayInvalidMessages(form: FormInstance, invalidInvites: InviteRecord[]): void {
	invalidInvites.forEach((invite) => {
		form.setFields([
			{
				errors: [ NOTIFICATIONS_COPY.FAILED_USER_INVITE ],
				name: `email-${invite.index}`
			}
		]);
	});
}

export const TeamInvites: React.FC = () => {
	const [ inviteForm ] = Form.useForm();
	const [ invites, setInvites ] = useState<InviteRecord[]>(defaultRecords);
	const { finished, invite, loading, result } = useInvite();

	const previousFinished = usePrevious(finished);

	useEffect(() => {
		if (!result) {
			return;
		}

		if (finished && previousFinished) {
			return;
		}

		const sentInvites = getInvitesToSend(invites, inviteForm);

		const invalidInvites = sentInvites.filter((currentInvite) =>
			result.invalidUsers.some((invalidUser) => invalidUser.email === currentInvite.email));

		const validInvites = difference(sentInvites, invalidInvites);

		displayInvalidMessages(inviteForm, invalidInvites);
		setInvites(rebuildInvitesWithProcessed(invites, validInvites));
	}, [ finished, inviteForm, invites, previousFinished, result ]);

	const handleSubmit = useCallback(() => {
		const invitesToSend = getInvitesToSend(invites, inviteForm);

		if (invitesToSend.length) {
			invite({
				users: invitesToSend as UserInvitation[]
			});
		}
	}, [ inviteForm, invite, invites ]);

	const handelValuesChange = useCallback((updatedValues: Record<string, string>) => {
		Object.keys(updatedValues).forEach((key) => {
			setInvites((currentInvites) => rebuildInvites(currentInvites, key, updatedValues[ key ]?.trim()));
		});
	}, []);

	return (
		<Form
			className='team-invites'
			form={ inviteForm }
			onValuesChange={ handelValuesChange }>
			{ invites.map((currentInvite) => {
				const { index } = currentInvite;
				const key = `invite-${index}`;
				const firstName = `firstName-${index}`;
				const lastName = `lastName-${index}`;
				const email = `email-${index}`;

				const { processed } = currentInvite;

				return (
					<Row
						gutter={ Number(css.size8) }
						key={ key }>
						<Col span={ 1 }>
							<When condition={ processed }>
								<CheckCircleOutlined className='processed-indicator' />
							</When>
						</Col>
						<Col span={ 6 }>
							<FirstName
								disabled={ processed || loading }
								name={ firstName }
								size='large' />
						</Col>
						<Col span={ 6 }>
							<LastName
								disabled={ processed || loading }
								name={ lastName }
								size='large' />
						</Col>
						<Col span={ 9 }>
							<Email
								disabled={ processed || loading }
								name={ email }
								size='large' />
						</Col>
					</Row>
				);
			}) }
			<Row justify='center'>
				<Space size={ Number(css.size24) }>
					<Button
						disabled={ loading }
						id='onboarding-invite-button'
						onClick={ handleSubmit }
						size='large'
						type='primary'>
						{ ONBOARDING_SYNC_COPY.SEND_INVITES }
					</Button>
				</Space>
			</Row>
			<When condition={ finished }>
				<p>{ ONBOARDING_SYNC_COPY.INVITES_SENT }</p>
			</When>
		</Form>
	);
};
